import { Button, Modal, Tabs, Tab } from "react-bootstrap";
import { useEffect, useState } from "react";
import { Problem } from "../../api/backendApi";
import ErrorDisplay from "../../common/ErrorDisplay";
import LoadingDisplay from "../../common/LoadingDisplay";
import {
  CrsEntryModel,
  DatEntryModel,
  GdvEntryModel,
  HsnTsnModel,
  hsntsnAPI,
} from "../../api/hsntsnApi";
import GdvHsnTsn from "./GdvHsnHsn";
import DatHsnTsn from "./DatHsnTsn";
import CrsHsnTsn from "./CrsHsnTsn";

interface HsnTsnModalProps {
  recId: number;
  onCancel: () => void;
  onSave: (hasSelection: boolean) => void;
  modalText: string;
  disabled: boolean;
}

export function HsnTsnModal({
  recId,
  onCancel,
  onSave,
  modalText,
  disabled,
}: HsnTsnModalProps) {
  const [gdvEntries, setGdvEntries] = useState<GdvEntryModel[] | undefined>();
  const [datEntries, setDatEntries] = useState<DatEntryModel[] | undefined>();
  const [crsEntries, setCrsEntries] = useState<CrsEntryModel[] | undefined>();
  const [selectedEntries, setSelectedEntries] = useState<HsnTsnModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Problem | undefined>(undefined);

  useEffect(() => {
    async function loadModal(recId: number) {
      loadHsnTsnView(recId);
    }
    loadModal(recId);
  }, [recId]);

  const loadHsnTsnView = async (recId: number) => {
    setLoading(true);
    const data = await hsntsnAPI.getHsnTsnView(recId);

    if (data.ok) {
      setError(undefined);
      setSelectedEntries(data.value.selectedEntries ?? []);
      setGdvEntries(data.value.gdvEntries);
      setDatEntries(data.value.datEntries);
      setCrsEntries(data.value.crsEntries);
    } else {
      setError(data.problem);
    }

    setLoading(false);
  };

  const setIsIncluded = (
    hsn: string | undefined,
    tsn: string | undefined,
    value: boolean,
  ) => {
    let currentValueIndex = selectedEntries.findIndex(
      (x) => x.hsn === hsn && x.tsn === tsn,
    );

    if (value && currentValueIndex < 0) {
      // not there, add it
      setSelectedEntries(selectedEntries.concat({ hsn, tsn }));
    } else if (!value && currentValueIndex >= 0) {
      // remove it
      setSelectedEntries(
        selectedEntries.filter((x, index) => index !== currentValueIndex),
      );
    }
  };

  const saveChanges = async () => {
    const selectedCrsEntries =
      crsEntries?.filter(
        (x) =>
          selectedEntries.findIndex(
            (y) => y.hsn === x.hsn && y.tsn === x.tsn,
          ) >= 0,
      ) ?? [];

    let result = await hsntsnAPI.updateHsnTsnEntries(
      recId,
      selectedEntries,
      selectedCrsEntries,
    );

    if (result.ok) {
      setError(undefined);
      onSave(selectedEntries.length > 0);
      onCancel();
    } else {
      setError(result.problem);
    }
  };

  return (
    <>
      <Modal onHide={() => onCancel()} show={true} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{modalText}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoadingDisplay loading={loading} />
          <ErrorDisplay error={error} />
          {!loading && (
            <>
              <Tabs defaultActiveKey="gdv" id="hsntsn-tabs" className="mb-3">
                <Tab eventKey="gdv" title="GDV">
                  {gdvEntries && (
                    <GdvHsnTsn
                      entries={gdvEntries}
                      selectedEntries={selectedEntries}
                      selectionChanged={setIsIncluded}
                    />
                  )}
                </Tab>
                <Tab eventKey="dat" title="DAT">
                  {datEntries && (
                    <DatHsnTsn
                      entries={datEntries}
                      selectedEntries={selectedEntries}
                      selectionChanged={setIsIncluded}
                    />
                  )}
                </Tab>
                <Tab eventKey="crs" title="CRS">
                  {crsEntries && (
                    <CrsHsnTsn
                      entries={crsEntries}
                      selectedEntries={selectedEntries}
                      disabled={disabled}
                      selectionChanged={setIsIncluded}
                      crsEntriesModified={(x) => setCrsEntries(x)}
                    />
                  )}
                </Tab>
              </Tabs>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!disabled && (
            <Button type="button" onClick={() => saveChanges()}>
              Save
            </Button>
          )}
          <Button variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default HsnTsnModal;
